import { getDate, getMonth, getYear, set as setDate } from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import { DEFAULT_TIMEZONE } from 'garaje/utils/date-fns-tz-utilities';
import SimpleTime from 'garaje/utils/simple-time';

/**
 * Combines a Date instance with a time string (e.g., "3:15 pm") in the specified timezone.
 *
 * @param date - The date to use (only the year, month, and day are used)
 * @param timeString - Time string in 12-hour format (e.g., "3:15 pm", "11:45 am")
 * @param timezone - IANA timezone string (e.g., "America/Los_Angeles")
 * @returns A new Date object representing the combined date and time in the specified timezone
 *
 * @example
 * const date = new Date('2025-10-13');
 * const result = combineDateAndTime(date, '3:15 pm', 'America/Los_Angeles');
 * // Returns: October 13, 2025 at 3:15 PM Pacific Time
 */
function combineDateAndTime(date: Date, timeString: string, timezone: string = DEFAULT_TIMEZONE): Date {
  const time = SimpleTime.parse12Hour(timeString);
  const zonedDate = toZonedTime(date, timezone);
  const dateWithTime = setDate(zonedDate, {
    hours: time.hour,
    minutes: time.minute,
    seconds: 0,
    milliseconds: 0,
  });

  return fromZonedTime(dateWithTime, timezone);
}

/**
 * Updates the year, month, and day of a date while preserving its time (hours, minutes, seconds, milliseconds)
 * in the specified timezone.
 *
 * @param dateToUpdate - The date whose year/month/day should be updated (time portion is preserved)
 * @param newDay - The date to extract the year/month/day from
 * @param timezone - IANA timezone string (e.g., "America/Los_Angeles")
 * @returns A new Date object with the updated year/month/day but original time in the specified timezone
 *
 * @example
 * const original = new Date('2025-10-13T15:30:45.123Z'); // Oct 13, 2025 at 3:30:45.123 PM
 * const newDate = new Date('2025-12-25T00:00:00.000Z'); // Dec 25, 2025
 * const result = updateDateToNewDay(original, newDate, 'America/Los_Angeles');
 * // Returns: December 25, 2025 at 3:30:45.123 PM Pacific Time
 */
function updateDateToNewDay(dateToUpdate: Date, newDay: Date, timezone: string = DEFAULT_TIMEZONE): Date {
  const zonedDateToUpdate = toZonedTime(dateToUpdate, timezone);
  const zonedNewDay = toZonedTime(newDay, timezone);
  const updatedDate = setDate(zonedDateToUpdate, {
    year: getYear(zonedNewDay),
    month: getMonth(zonedNewDay),
    date: getDate(zonedNewDay),
  });

  return fromZonedTime(updatedDate, timezone);
}

export { combineDateAndTime, updateDateToNewDay };
